import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import AdminDashboard from './pages/admin/AdminDashboard';
import CompanyHeadDashboard from './pages/company-head/CompanyHeadDashboard';
import LoginPage from './pages/LoginPage';
import EmailVerificationPage from './pages/EmailVerificationPage';
import PasswordResetPage from './pages/PasswordResetPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ProtectedRoute from './components/ProtectedRoute';
import EmployeeDashboard from './pages/employee/EmployeeDashboard';
import TilesDeliveryOrder from './components/TilesDeliveryOrder';
import TilesDeliveryOrderDiscount from './components/TilesDeliveryOrderDiscount';
import DeliveryOrder from './components/DeliveryOrder';
import DeliveryOrderDiscount from './components/DeliveryOrderDiscount';
import GoodsReturnReview from './components/GoodsReturnReview';
import PackingSlip from './components/PackingSlip';
import AuthProvider from './components/AuthContext';
import QuotationReview from './components/QuotationReview';
import QuotationNewReview from './components/QuotationNewReview';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsofUse from './pages/TermsofUse';
import SuperAdminDashboard from './pages/super-admin/SuperAdminDashboard';
import TallyQuotationReview from './components/TallyQuotationReview';
import ProformaInvoiceReview from './components/ProformaInvoiceReview';
import DomainGuard from './components/DomainGuard';
import TallyItemUsageReport from './components/TallyItemUsageReport';

function App() {
  return (
    <Router>
      <AuthProvider>
        <Routes>
          {/* Add both root and /login route for the login page */}
          <Route path="/" element={<LoginPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/email-verification" element={<EmailVerificationPage />} />
          <Route path="/password-reset" element={<PasswordResetPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          
          {/* Public routes remain the same */}
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-use" element={<TermsofUse />} />
          <Route path="/quotation/:id" element={<QuotationReview />} />
          <Route path="/print-quotation-new/:id" element={<QuotationNewReview />} />
          <Route path="/print-quotation-new/:id/:versionNumber" element={<QuotationNewReview />} />
          <Route path="/tiles-delivery-orders/:id" element={<TilesDeliveryOrder />} />
          <Route path="/tiles-delivery-orders-discount/:id" element={<TilesDeliveryOrderDiscount />} />
          <Route path="/delivery-orders/:id" element={<DeliveryOrder />} />
          <Route path="/delivery-orders-details/:id" element={<DeliveryOrderDiscount />} />
          <Route path="/goods-return/:id" element={<GoodsReturnReview />} />
          <Route path="/packing-slip/:packingSlipNumber" element={<PackingSlip />} />
          <Route path="/tally-quotation/:id" element={<TallyQuotationReview />} />
          <Route path="/proforma-invoice-review/:id" element={<ProformaInvoiceReview />} />
          <Route path="/tally-item-usage-report/:id" element={<TallyItemUsageReport />} />

          {/* Admin routes with DomainGuard */}
          <Route path="/admin">
            <Route path="dashboard" element={
              <ProtectedRoute role="Admin">
                <DomainGuard accessType="admin">
                  <AdminDashboard />
                </DomainGuard>
              </ProtectedRoute>
            } />
            <Route path=":feature" element={
              <ProtectedRoute role="Admin">
                <DomainGuard accessType="admin">
                  <AdminDashboard />
                </DomainGuard>
              </ProtectedRoute>
            } />
            <Route path="campaign-management" element={<AdminDashboard />} />
          </Route>

          {/* Company Head routes with DomainGuard */}
          <Route path="/company-head">
            <Route path="dashboard" element={
              <ProtectedRoute role="CompanyHead">
                <DomainGuard accessType="companyHead">
                  <CompanyHeadDashboard />
                </DomainGuard>
              </ProtectedRoute>
            } />
            <Route path=":feature" element={
              <ProtectedRoute role="CompanyHead">
                <DomainGuard accessType="companyHead">
                  <CompanyHeadDashboard />
                </DomainGuard>
              </ProtectedRoute>
            } />
            <Route path="role-management" element={
              <ProtectedRoute role="CompanyHead">
                <DomainGuard accessType="companyHead" requiredAccess="role-management">
                  <CompanyHeadDashboard />
                </DomainGuard>
              </ProtectedRoute>
            } />
          </Route>

          {/* Employee routes with DomainGuard */}
          <Route path="/employee">
            <Route path="dashboard" element={
              <ProtectedRoute role="Employee">
                <DomainGuard accessType="employee">
                  <EmployeeDashboard/>
                </DomainGuard>
              </ProtectedRoute>
            } />
            <Route path=":feature" element={
              <ProtectedRoute role="Employee">
                <DomainGuard accessType="employee">
                  <EmployeeDashboard/>
                </DomainGuard>
              </ProtectedRoute>
            } />
          </Route>

          {/* Super Admin routes remain the same */}
          <Route path="/super-admin">
            <Route 
              path="dashboard" 
              element={
                <ProtectedRoute role="SuperAdmin">
                  <SuperAdminDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="companies" 
              element={
                <ProtectedRoute role="SuperAdmin">
                  <SuperAdminDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="company-heads" 
              element={
                <ProtectedRoute role="SuperAdmin">
                  <SuperAdminDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="locations" 
              element={
                <ProtectedRoute role="SuperAdmin">
                  <SuperAdminDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="categories" 
              element={
                <ProtectedRoute role="SuperAdmin">
                  <SuperAdminDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="brands" 
              element={
                <ProtectedRoute role="SuperAdmin">
                  <SuperAdminDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="logs" 
              element={
                <ProtectedRoute role="SuperAdmin">
                  <SuperAdminDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="firms" 
              element={
                <ProtectedRoute role="SuperAdmin">
                  <SuperAdminDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="admins" 
              element={
                <ProtectedRoute role="SuperAdmin">
                  <SuperAdminDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="data-export" 
              element={
                <ProtectedRoute role="SuperAdmin">
                  <SuperAdminDashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="support" 
              element={
                <ProtectedRoute role="SuperAdmin">
                  <SuperAdminDashboard />
                </ProtectedRoute>
              } 
            />
          </Route>
        </Routes>
      </AuthProvider>
    </Router>
  );
}

export default App;